import React, { useContext, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Table, Tag, Button, Select } from "antd";
import { useHistory, useParams } from "react-router-dom";
import Header from "../components/header/HeaderHome";
import { GlobalContext } from "../hook/GlobalHook";
import { GetSchoolCourseProgressAction, GetCoursesBySchoolIdAction } from "../actions";

const { Option } = Select;
const { Column } = Table;

function formatScore(score) {
  if (!score || !score.total) return "-";
  return `${score.correct}/${score.total}`;
}

function formatPercent(score) {
  if (!score || !score.total) return "-";
  return `${score.percent}%`;
}

function statusTag(status) {
  const color =
    status === "PASS" ? "green" : status === "FAIL" ? "red" : "default";
  return <Tag color={color}>{status || "-"}</Tag>;
}

function csvEscape(value) {
  const text = value == null ? "" : String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

// Keep scores like 8/6 as text so Excel does not turn them into dates (8-Jun)
function excelSafeScore(value) {
  if (value == null || value === "-") return "-";
  const text = String(value);
  if (text.indexOf("/") !== -1) {
    return `="${text}"`;
  }
  return text;
}

function rowsForCsvDownload(rows) {
  return (rows || []).map((row) => {
    const next = {};
    Object.keys(row).forEach((field) => {
      if (field === "key") return;
      if (field === "score") {
        next[field] = excelSafeScore(row[field]);
      } else {
        next[field] = row[field];
      }
    });
    return next;
  });
}

function downloadUtf8Csv(rows, filename) {
  const exportRows = rowsForCsvDownload(rows);
  if (!exportRows.length) return;

  const headers = Object.keys(exportRows[0]);
  const lines = [headers.join(",")];
  exportRows.forEach((row) => {
    lines.push(headers.map((h) => csvEscape(row[h])).join(","));
  });

  // BOM so Excel opens Thai/UTF-8 correctly
  const csvContent = "\uFEFF" + lines.join("\r\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || "teacher-dashboard.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function SchoolTeacherCourse() {
  const GlobalHook = useContext(GlobalContext);
  const history = useHistory();
  const { courseId } = useParams();
  const [progress, setProgress] = useState(null);
  const [selectedUid, setSelectedUid] = useState("all");
  const [viewMode, setViewMode] = useState("quiz");
  const [courses, setCourses] = useState([]);
  const schoolId =
    GlobalHook.getGlobalUser?.schoolTeacherSchoolId ||
    GlobalHook.getGlobalUser?.schoolAdminId;

  useEffect(() => {
    if (!schoolId) {
      history.push("/");
      return;
    }

    GetCoursesBySchoolIdAction(schoolId).then((list) => {
      setCourses(list || []);
    });
  }, [schoolId]);

  useEffect(() => {
    if (!schoolId) {
      history.push("/");
      return;
    }

    GlobalHook.setGlobalLoading(true);
    GetSchoolCourseProgressAction(schoolId, courseId)
      .then((data) => {
        setProgress(data);
        setSelectedUid("all");
      })
      .catch(() => history.push("/schoolteacher"))
      .finally(() => GlobalHook.setGlobalLoading(false));
  }, [courseId, schoolId]);

  const students = progress?.students || [];
  const selectedStudent =
    selectedUid === "all"
      ? null
      : students.find((s) => s.uid === selectedUid) || null;

  const progressRows = useMemo(() => {
    const list = selectedStudent ? [selectedStudent] : students;
    return list.map((s, i) => ({
      key: s.uid || i,
      name: s.name || "-",
      email: s.email || "-",
      school: s.school || "-",
      lessons: `${s.completed || 0}/${s.total || 0}`,
      quizzes: `${s.quizzesPassed || 0}/${s.quizzesTotal || 0}`,
      percent: `${Number(s.percent || 0).toFixed(1)}%`,
    }));
  }, [students, selectedStudent]);

  const quizRows = useMemo(() => {
    const rows = [];
    const list = selectedStudent ? [selectedStudent] : students;

    list.forEach((student) => {
      (student.quizStatuses || []).forEach((quiz) => {
        const best = quiz.bestScore;
        rows.push({
          key: `${student.uid}-${quiz.mediaId}`,
          studentName: student.name || "-",
          email: student.email || "-",
          quizTitle: quiz.title,
          score: formatScore(best),
          percent: formatPercent(best),
          attempts: quiz.attempts || 0,
          status: quiz.status || "-",
        });
      });
    });

    return rows;
  }, [students, selectedStudent]);

  const attemptRows = useMemo(() => {
    if (!selectedStudent) return [];
    const rows = [];
    (selectedStudent.quizStatuses || []).forEach((quiz) => {
      (quiz.attemptDetails || []).forEach((attempt, index) => {
        const date = attempt.endTime
          ? new Date(Number(attempt.endTime))
          : null;
        rows.push({
          key: `${selectedStudent.uid}-${quiz.mediaId}-${index}`,
          quizTitle: quiz.title,
          attemptNo: quiz.attemptDetails.length - index,
          score: formatScore(attempt),
          percent: formatPercent(attempt),
          status: attempt.passResult ? "PASS" : "FAIL",
          date: date
            ? `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()} ${date.getHours()}:${("0" + date.getMinutes()).slice(-2)}`
            : "-",
        });
      });
    });
    return rows;
  }, [selectedStudent]);

  const downloadData =
    viewMode === "progress"
      ? progressRows
      : selectedStudent
        ? attemptRows
        : quizRows;

  if (!progress) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>Studysabai: {progress.course.courseName}</title>
      </Helmet>
      <Header />
      <div className="w-full flex flex-col items-center py-6 px-4">
        <Button
          className="mb-4 self-start"
          onClick={() => history.push("/schoolteacher")}
        >
          Back to courses
        </Button>

        <div className="w-10/12 rounded-lg text-center text-white py-2 text-2xl font-bold mb-4 bg-orange-500">
          Dashboard — {progress.course.courseName}
        </div>

        <div
          className="mb-6"
          style={{ width: "90%", maxWidth: "1100px" }}
        >
          <div className="mb-1 font-bold">Filter by course</div>
          <Select
            showSearch
            optionFilterProp="children"
            value={courseId}
            onChange={(nextCourseId) => {
              if (String(nextCourseId) !== String(courseId)) {
                history.push(`/schoolteacher/${nextCourseId}`);
              }
            }}
            style={{ width: "100%", maxWidth: "520px" }}
            placeholder="Select course"
          >
            {courses.map((c) => (
              <Option key={c._id} value={c._id}>
                {c.courseName}
              </Option>
            ))}
          </Select>
        </div>

        <div
          className="bg-gray-200 p-6 rounded-lg mb-6"
          style={{ width: "90%", maxWidth: "1100px" }}
        >
          <div
            className="rounded-lg text-center text-white py-2 text-xl font-bold bg-purple-500 mx-auto mb-4"
            style={{ width: "120px" }}
          >
            Student
          </div>

          <Select
            value={selectedUid}
            onChange={setSelectedUid}
            style={{ width: "100%", maxWidth: "520px" }}
            showSearch
            optionFilterProp="children"
          >
            <Option value="all">All Students</Option>
            {students.map((s) => (
              <Option key={s.uid} value={s.uid}>
                {(s.name || "-") + " — " + (s.email || "")}
              </Option>
            ))}
          </Select>

          {selectedStudent && (
            <div className="bg-white rounded-lg p-4 mt-4">
              <div>Name: {selectedStudent.name || "-"}</div>
              <div>Email: {selectedStudent.email || "-"}</div>
              <div>School: {selectedStudent.school || "-"}</div>
              <div>
                Quizzes passed: {selectedStudent.quizzesPassed || 0}/
                {selectedStudent.quizzesTotal || 0}
              </div>
            </div>
          )}
        </div>

        <div
          className="bg-gray-200 p-6 rounded-lg"
          style={{ width: "90%", maxWidth: "1100px" }}
        >
          <div
            className="rounded-lg text-center text-white py-2 text-xl font-bold bg-blue-500 mx-auto mb-4"
            style={{ width: "120px" }}
          >
            Statistic
          </div>

          <div className="mb-2 font-bold">เลือกการวิเคราะห์ผล</div>
          <Select
            value={viewMode}
            onChange={setViewMode}
            style={{ minWidth: 210, marginBottom: 16 }}
          >
            <Option value="quiz">ผลแบบทดสอบ</Option>
            <Option value="progress">ความคืบหน้า</Option>
            {selectedUid !== "all" && (
              <Option value="attempts">ประวัติการสอบ</Option>
            )}
          </Select>

          {viewMode === "progress" && (
            <>
              <div className="mb-3">
                สรุปผลความคืบหน้าของผู้เรียน
                {selectedStudent ? "" : "ทั้งหมด"}
              </div>
              <Table dataSource={progressRows} pagination={{ pageSize: 20 }}>
                <Column title="Name" dataIndex="name" key="name" />
                <Column title="Email" dataIndex="email" key="email" />
                <Column title="Lessons" dataIndex="lessons" key="lessons" />
                <Column title="Quizzes" dataIndex="quizzes" key="quizzes" />
                <Column title="Progress" dataIndex="percent" key="percent" />
              </Table>
            </>
          )}

          {viewMode === "quiz" && (
            <>
              <div className="mb-3">
                สรุปผลแบบทดสอบ (คะแนนดีที่สุด)
                {selectedStudent ? "" : " — ผู้เรียนทั้งหมด"}
              </div>
              <Table dataSource={quizRows} pagination={{ pageSize: 20 }}>
                {!selectedStudent && (
                  <Column title="Student" dataIndex="studentName" key="studentName" />
                )}
                {!selectedStudent && (
                  <Column title="Email" dataIndex="email" key="email" />
                )}
                <Column title="Quiz" dataIndex="quizTitle" key="quizTitle" />
                <Column title="Score" dataIndex="score" key="score" />
                <Column title="Percent" dataIndex="percent" key="percent" />
                <Column title="Attempts" dataIndex="attempts" key="attempts" />
                <Column
                  title="Status"
                  dataIndex="status"
                  key="status"
                  render={(status) => statusTag(status)}
                />
              </Table>
            </>
          )}

          {viewMode === "attempts" && selectedStudent && (
            <>
              <div className="mb-3">
                ประวัติการสอบของ {selectedStudent.name || selectedStudent.email}
              </div>
              <Table dataSource={attemptRows} pagination={{ pageSize: 20 }}>
                <Column title="Quiz" dataIndex="quizTitle" key="quizTitle" />
                <Column title="Attempt" dataIndex="attemptNo" key="attemptNo" />
                <Column title="Score" dataIndex="score" key="score" />
                <Column title="Percent" dataIndex="percent" key="percent" />
                <Column
                  title="Status"
                  dataIndex="status"
                  key="status"
                  render={(status) => statusTag(status)}
                />
                <Column title="Date" dataIndex="date" key="date" />
              </Table>
            </>
          )}

          <div className="flex justify-center mt-4">
            <button
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
              onClick={() =>
                downloadUtf8Csv(
                  downloadData,
                  `${progress.course.courseName || "teacher"}-dashboard.csv`
                )
              }
            >
              Download Data
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
